import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";

import { AuthLayout } from "@/components/auth";
import { api } from "@/lib/api";

type ApproveState = "approving" | "approved" | "invalid";

/**
 * Landing page for the "Approve sign-in" button in the OTP email.
 *
 * The approval fires as a JS POST on mount — never on the GET itself — so a
 * mail scanner prefetching the link cannot approve a login. This device never
 * receives a session; the browser that requested the code claims it via poll.
 */
export function Approve() {
  const [searchParams] = useSearchParams();
  const [state, setState] = useState<ApproveState>("approving");
  // React 18 StrictMode double-mounts effects in dev; the endpoint is
  // idempotent, but one request is still the right number
  const firedRef = useRef(false);

  useEffect(() => {
    if (firedRef.current) return;
    firedRef.current = true;

    const requestId = searchParams.get("rid");
    const token = searchParams.get("t");
    if (!requestId || !token) {
      setState("invalid");
      return;
    }

    api
      .post("/auth/otp/approve", { request_id: requestId, token })
      .then(() => setState("approved"))
      .catch(() => setState("invalid"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AuthLayout
      title="Approve sign-in"
      subtitle="Confirming the sign-in you requested"
    >
      <div className="flex flex-col items-center gap-4 py-6 text-center">
        {state === "approving" && (
          <>
            <Loader2 className="h-10 w-10 animate-spin text-primary" />
            <p className="text-sm text-muted-foreground">Approving your sign-in…</p>
          </>
        )}
        {state === "approved" && (
          <>
            <CheckCircle2 className="h-10 w-10 text-green-500" />
            <div className="space-y-1">
              <p className="font-medium">Sign-in approved</p>
              <p className="text-sm text-muted-foreground">
                Return to the tab where you requested the code — it will sign in by itself. You
                can close this page.
              </p>
            </div>
          </>
        )}
        {state === "invalid" && (
          <>
            <XCircle className="h-10 w-10 text-destructive" />
            <div className="space-y-1">
              <p className="font-medium">This sign-in link is invalid or has expired</p>
              <p className="text-sm text-muted-foreground">
                Request a new code from the sign-in page and use the fresh email.
              </p>
            </div>
          </>
        )}
      </div>
    </AuthLayout>
  );
}

export default Approve;
